import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  GitBranch,
  Play,
  Square,
  RefreshCw,
  Loader2,
  ChevronDown,
  CheckCircle2,
  Clock,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export const WorkflowPanel = ({ transaction, onUpdate }) => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [showChange, setShowChange] = useState(false);

  const currentTemplateId = transaction?.workflow_template_id;
  const tasks = transaction?.tasks || [];
  const workflowTasks = tasks.filter((t) => t.reference_date !== "manual");
  const completedCount = workflowTasks.filter((t) => t.status === "completed").length;
  const pendingCount = workflowTasks.filter((t) => t.status === "pending").length;
  const overdueCount = workflowTasks.filter((t) => {
    if (t.status === "completed") return false;
    const due = (t.due_date || "").slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return due && due < today;
  }).length;

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${API}/workflows`, { withCredentials: true });
      setWorkflows(res.data);
    } catch (err) {
      console.error("Failed to fetch workflows:", err);
    } finally {
      setLoading(false);
    }
  };

  const currentWorkflow = workflows.find((w) => w.template_id === currentTemplateId);
  const filteredWorkflows = useMemo(
    () => workflows.filter((w) => w.template_id !== currentTemplateId),
    [workflows, currentTemplateId]
  );

  const handleApply = async (templateId) => {
    if (!templateId) return;
    setActing(true);
    try {
      const action = currentTemplateId ? "change" : "apply";
      const res = await axios.put(
        `${API}/transactions/${transaction.transaction_id}/workflow`,
        { workflow_template_id: templateId, action },
        { withCredentials: true }
      );
      toast.success(action === "apply" ? "Workflow applied" : "Workflow changed");
      setShowChange(false);
      setSelectedId("");
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update workflow");
    } finally {
      setActing(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove this workflow? Pending workflow tasks will be deleted. Completed and manual tasks are kept.")) return;
    setActing(true);
    try {
      const res = await axios.put(
        `${API}/transactions/${transaction.transaction_id}/workflow`,
        { action: "remove" },
        { withCredentials: true }
      );
      toast.success("Workflow removed");
      setShowChange(false);
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove workflow");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-surface" data-testid="workflow-panel">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--t-text-subtle)" }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-surface" data-testid="workflow-panel">
      <CardHeader className="border-b border-[var(--t-border)] py-3 px-5">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[var(--t-text)] [font-family:var(--t-heading-font)] uppercase tracking-wider flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Workflow
          </CardTitle>
          {currentTemplateId && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs"
                style={{ borderColor: "var(--t-border)", color: "var(--t-text-muted)" }}
                onClick={() => setShowChange(!showChange)}
                data-testid="change-workflow-btn"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Change
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 text-xs text-red-600 hover:text-red-700 hover:border-red-300"
                style={{ borderColor: "var(--t-border)" }}
                onClick={handleRemove}
                disabled={acting}
                data-testid="remove-workflow-btn"
              >
                <Square className="w-3 h-3 mr-1" />
                Remove
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {currentTemplateId && currentWorkflow ? (
          <div>
            {/* Current workflow info */}
            <div className="flex items-center gap-3 mb-3" data-testid="current-workflow-info">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--t-primary)", opacity: 0.15 }}>
                <GitBranch className="w-4.5 h-4.5" style={{ color: "var(--t-primary)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: "var(--t-text)" }}>{currentWorkflow.name}</p>
                {currentWorkflow.description && (
                  <p className="text-xs mt-0.5 truncate" style={{ color: "var(--t-text-muted)" }}>{currentWorkflow.description}</p>
                )}
              </div>
            </div>

            {/* Task progress */}
            {workflowTasks.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center gap-4 text-xs mb-2">
                  <span className="flex items-center gap-1" style={{ color: "#059669" }}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {completedCount} done
                  </span>
                  <span className="flex items-center gap-1" style={{ color: "var(--t-text-muted)" }}>
                    <Clock className="w-3.5 h-3.5" /> {pendingCount} pending
                  </span>
                  {overdueCount > 0 && (
                    <span className="flex items-center gap-1" style={{ color: "#dc2626" }}>
                      <AlertCircle className="w-3.5 h-3.5" /> {overdueCount} overdue
                    </span>
                  )}
                </div>
                {/* Progress bar */}
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--t-border)" }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${workflowTasks.length > 0 ? (completedCount / workflowTasks.length) * 100 : 0}%`,
                      background: completedCount === workflowTasks.length ? "#059669" : "var(--t-primary)",
                    }}
                  />
                </div>
                <p className="text-[11px] mt-1.5 text-right" style={{ color: "var(--t-text-subtle)" }}>
                  {workflowTasks.length > 0 ? Math.round((completedCount / workflowTasks.length) * 100) : 0}% complete
                </p>
              </div>
            )}

            {/* Change workflow dropdown */}
            {showChange && (
              <div className="mt-4 pt-3" style={{ borderTop: "1px solid var(--t-border-light)" }}>
                <p className="text-xs font-medium mb-2" style={{ color: "var(--t-text-muted)" }}>Switch to a different workflow:</p>
                <div className="flex gap-2">
                  <Select value={selectedId} onValueChange={setSelectedId}>
                    <SelectTrigger className="flex-1 h-8 text-xs" data-testid="workflow-change-select">
                      <SelectValue placeholder="Select workflow..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredWorkflows.map((w) => (
                        <SelectItem key={w.template_id} value={w.template_id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    className="h-8 px-3 text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)] text-white"
                    onClick={() => handleApply(selectedId)}
                    disabled={!selectedId || acting}
                    data-testid="apply-workflow-change-btn"
                  >
                    {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                  </Button>
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: "var(--t-text-subtle)" }}>
                  Pending tasks from the current workflow will be replaced. Completed and manual tasks are kept.
                </p>
              </div>
            )}
          </div>
        ) : (
          /* No workflow assigned */
          <div data-testid="no-workflow-assigned">
            <p className="text-sm mb-3" style={{ color: "var(--t-text-muted)" }}>No workflow assigned to this transaction.</p>
            {workflows.length > 0 ? (
              <div className="flex gap-2">
                <Select value={selectedId} onValueChange={setSelectedId}>
                  <SelectTrigger className="flex-1 h-8 text-xs" data-testid="workflow-apply-select">
                    <SelectValue placeholder="Select a workflow..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workflows.map((w) => (
                      <SelectItem key={w.template_id} value={w.template_id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-8 px-3 text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)] text-white"
                  onClick={() => handleApply(selectedId)}
                  disabled={!selectedId || acting}
                  data-testid="apply-workflow-btn"
                >
                  {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3 h-3 mr-1" /> Apply</>}
                </Button>
              </div>
            ) : (
              <p className="text-xs" style={{ color: "var(--t-text-subtle)" }}>
                No workflow templates available. Create one in the Workflows section.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
